import React, { useMemo } from 'react';
import { AuditRecord } from '../../../types';
import {
  computeDrawdown, computeStreak, computeExecutionQuality,
  computeBehavioral, buildEquityCurve,
} from '../../../services/auditService';
import { KPI, Section, Th, Td, tbStyle } from './AuditTablePrimitives';
import { r2, colorR } from '../utils/formatters';

export function PerformanceView({ auditRecords }: { auditRecords: AuditRecord[] }) {
  const closed = auditRecords.filter(r => r.status === 'closed' && r.realizedR !== null);
  const dd     = useMemo(() => computeDrawdown(auditRecords),         [auditRecords]);
  const streak = useMemo(() => computeStreak(auditRecords),           [auditRecords]);
  const exQual = useMemo(() => computeExecutionQuality(auditRecords), [auditRecords]);
  const behav  = useMemo(() => computeBehavioral(auditRecords),       [auditRecords]);
  const curve  = useMemo(() => buildEquityCurve(auditRecords),        [auditRecords]);

  if (!closed.length) {
    return (
      <div style={{ padding: 24, color: 'var(--t3)', textAlign: 'center', fontSize: 12 }}>
        No closed records yet. Performance analytics populate as you close audit records.
      </div>
    );
  }

  const streakColor = streak.currentStreak > 0 ? 'var(--green)'
    : streak.currentStreak < 0 ? 'var(--red)' : 'var(--t2)';
  const streakLabel = streak.currentStreak > 0
    ? `${streak.currentStreak}W streak`
    : streak.currentStreak < 0
    ? `${Math.abs(streak.currentStreak)}L streak`
    : 'No streak';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <KPI label="Peak Equity"    value={dd.peakEquityR !== null ? r2(dd.peakEquityR) : '—'}     color="var(--cyan)" />
        <KPI label="Current Equity" value={dd.currentEquityR !== null ? r2(dd.currentEquityR) : '—'} color={colorR(dd.currentEquityR)} />
        <KPI label="Max Drawdown"   value={dd.maxDrawdown !== null ? r2(dd.maxDrawdown) : '—'}     color={dd.maxDrawdown !== null && dd.maxDrawdown < -2 ? 'var(--red)' : 'var(--amber)'} />
        <KPI label="Curr Drawdown"  value={dd.currentDrawdown !== null ? r2(dd.currentDrawdown) : '—'} color={colorR(dd.currentDrawdown)} />
        <KPI label="Streak"         value={streakLabel}              color={streakColor} />
        <KPI label="Max Win Run"    value={`${streak.maxWinStreak}W`}  color="var(--green)" />
        <KPI label="Max Loss Run"   value={`${streak.maxLossStreak}L`} color="var(--red)" />
        {streak.bestWinningSetup && (
          <KPI label="Top Setup (Wins)" value={streak.bestWinningSetup.substring(0, 18)} color="var(--cyan)" />
        )}
      </div>

      <Section title="📈 Equity Curve (cumulative R by trade)">
        {curve.length < 2 ? (
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>Need at least 2 closed trades.</div>
        ) : (
          <>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--t3)', marginBottom: 8 }}>
              {curve.map((pt, i) => (
                <span key={i}
                  title={`Trade ${pt.tradeIndex}: ${pt.cumulativeR > 0 ? '+' : ''}${pt.cumulativeR}R (${pt.outcome})`}
                  style={{ color: pt.outcome === 'win' ? 'var(--green)' : pt.outcome === 'loss' ? 'var(--red)' : 'var(--t3)' }}>
                  {pt.cumulativeR >= 0 ? '▲' : '▼'}
                </span>
              ))}
            </div>
            <table style={tbStyle}>
              <thead>
                <tr><Th>#</Th><Th>Setup</Th><Th>Outcome</Th><Th right>Trade R</Th><Th right>Cumulative R</Th></tr>
              </thead>
              <tbody>
                {curve.slice(-20).map(pt => {
                  const prevCum = curve[pt.tradeIndex - 2]?.cumulativeR ?? 0;
                  const tradeR  = +(pt.cumulativeR - prevCum).toFixed(2);
                  return (
                    <tr key={pt.tradeIndex}>
                      <Td>{pt.tradeIndex}</Td>
                      <Td><span style={{ fontSize: 9 }}>{pt.setupType.substring(0, 22)}</span></Td>
                      <Td><span style={{ fontSize: 10, color: pt.outcome === 'win' ? 'var(--green)' : pt.outcome === 'loss' ? 'var(--red)' : 'var(--t3)' }}>{pt.outcome}</span></Td>
                      <Td right color={colorR(tradeR)}>{r2(tradeR)}</Td>
                      <Td right color={colorR(pt.cumulativeR)}>{r2(pt.cumulativeR)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {curve.length > 20 && (
              <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 4 }}>Showing last 20 of {curve.length} trades.</div>
            )}
          </>
        )}
      </Section>

      <Section title="🎯 Execution Quality (Observational)">
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 2 }}>MFE Captured</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cyan)', fontFamily: 'var(--mono)' }}>
              {exQual.avgMFECaptured !== null ? `${(exQual.avgMFECaptured * 100).toFixed(0)}%` : '—'}
            </div>
            <div style={{ fontSize: 9, color: 'var(--t3)' }}>avg % of max gain captured</div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 2 }}>Avg MAE</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--amber)', fontFamily: 'var(--mono)' }}>
              {exQual.avgMAE !== null ? `${exQual.avgMAE.toFixed(2)}R` : '—'}
            </div>
            <div style={{ fontSize: 9, color: 'var(--t3)' }}>avg max adverse excursion</div>
          </div>
        </div>
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>
          Slippage and exit efficiency require manual entry vs planned price fields (future enhancement).
        </div>
      </Section>

      <Section title="🕐 Session Attribution">
        {behav.bestTimeOfDay || behav.worstTimeOfDay ? (
          <div style={{ display: 'flex', gap: 20 }}>
            <div>
              <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 2 }}>Best Session</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)', fontFamily: 'var(--mono)' }}>
                {behav.bestTimeOfDay ?? '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 2 }}>Worst Session</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', fontFamily: 'var(--mono)' }}>
                {behav.worstTimeOfDay ?? '—'}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>Insufficient data for session attribution.</div>
        )}
      </Section>

      <div style={{ fontSize: 9, color: 'var(--t3)', fontStyle: 'italic' }}>
        All figures are observational / simulated historical records. Past performance of any setup type
        does not guarantee future results. This system provides market analysis tools only.
      </div>
    </div>
  );
}
